import React from 'react';
import styled from '@emotion/styled';
import Table from '../../components/table';
import Tooltip from '../../components/tooltipV2/Tooltip';
import { useTranslation } from 'react-i18next';

type ContractSummaryTableProps = {
  retailPrice: string;
  tax: string;
  totalPurchaseAmount: string;
  timeToOwnership: string;
  paymentFrequency: string;
  numRemainingPayments: number;
  initialPayment: string;
  secondPayment: string;
  finalPayment: string | null;
  costOfLease: string | null;
  costOfOwnership: string;
  numSimilarPayments: number;
  cashPlusPrice: string;
  cashPlusTerm: { singular: string; plural: string };
  isVirtualCard: boolean;
};

const CapitalizedSpan = styled.span`
  display: inline-block;
  &::first-letter {
    text-transform: capitalize;
  }
`;

const getLabel = (labelText: string, tooltipID: string, tooltipContent: any) => {
  return (
    <div>
      {labelText} <Tooltip id={tooltipID} tooltipContent={tooltipContent} inline={true} size="12px" />
    </div>
  );
};

const ContractSummaryTable: React.FC<ContractSummaryTableProps> = ({
  retailPrice,
  tax,
  totalPurchaseAmount,
  timeToOwnership,
  paymentFrequency,
  numRemainingPayments,
  initialPayment,
  secondPayment,
  finalPayment,
  costOfLease,
  costOfOwnership,
  numSimilarPayments,
  cashPlusPrice,
  cashPlusTerm,
  isVirtualCard,
}) => {
  const { t } = useTranslation();

  const paymentFrequencyLabels: { [index: string]: string } = {
    weekly: t(($) => $.componentContractSummaryParts.weekly),
    biweekly: t(($) => $.componentContractSummaryParts.biweekly),
    monthly: t(($) => $.componentContractSummaryParts.monthly),
    semimonthly: t(($) => $.componentContractSummaryParts.semimonthly),
  };

  const RetailPriceTooltip = <div>{t(($) => $.pageContractSummaryEcomm.retailPriceTooltip)}</div>;
  const SalesTaxTooltip = <div>{t(($) => $.pageContractSummaryEcomm.salesTaxTooltip)}</div>;
  const TotalPurchaseAmountTooltip = <div>{t(($) => $.pageContractSummaryEcomm.totalPurchaseAmountTooltip)}</div>;
  const TimetoOwnershipTooltip = <div>{t(($) => $.pageContractSummaryEcomm.timeToOwnershipTooltip)}</div>;
  const PaymentFrequencyTooltip = <div>{t(($) => $.pageContractSummaryEcomm.paymentFrequencyTooltip)}</div>;
  const RemainingPaymentsTooltip = <div>{t(($) => $.pageContractSummaryEcomm.remainingPaymentsTooltip)}</div>;
  const InitialPaymentsTooltip = <div>{t(($) => $.pageContractSummaryEcomm.initialPaymentsTooltip)}</div>;
  const SecondPaymentsTooltip = <div>{t(($) => $.pageContractSummaryEcomm.paymentsTooltip)}</div>;
  const FinalPaymentTooltip = <div>{t(($) => $.pageContractSummaryEcomm.paymentsTooltip)}</div>;
  // Named rather than positional, so each language's template picks its own order for the term and price
  const CostOfLeaseTooltip = (values: { cpt: string; cpp: string }) => <div>{t(($) => $.pageContractSummaryEcomm.costOfLeaseTooltip, values)}</div>;
  const TotalOwnershipTooltip = (values: { cpt: string; cpp: string }) => <div>{t(($) => $.pageContractSummaryEcomm.totalOwnershipTooltip, values)}</div>;

  const leaseRows = [
    ...(!isVirtualCard
      ? [
          {
            label: getLabel(
              t(($) => $.pageContractSummaryEcomm.retailPrice),
              'retailPrice',
              RetailPriceTooltip,
            ), //`Lease amount`,
            value: retailPrice,
          },
        ]
      : []),
    ...(!isVirtualCard
      ? [
          {
            label: getLabel(
              t(($) => $.pageContractSummaryEcomm.salesTax),
              'salesTax',
              SalesTaxTooltip,
            ), //`Lease amount`,
            value: tax,
          },
        ]
      : []),
    {
      label: getLabel(
        t(($) => $.pageContractSummaryEcomm.totalPurchaseAmount),
        'totalPurchaseAmount',
        TotalPurchaseAmountTooltip,
      ), //`Lease amount`,
      value: totalPurchaseAmount,
    },
    {
      label: getLabel(
        t(($) => $.pageContractSummaryEcomm.timeToOwnership),
        'timeToOwnership',
        TimetoOwnershipTooltip,
      ),
      value: timeToOwnership,
    },
    {
      label: getLabel(
        t(($) => $.pageContractSummaryEcomm.paymentFrequency),
        'paymentFrequency',
        PaymentFrequencyTooltip,
      ),
      value: <CapitalizedSpan>{paymentFrequencyLabels[paymentFrequency]}</CapitalizedSpan>,
    },
    {
      label: getLabel(
        t(($) => $.pageContractSummaryEcomm.remainingPayments),
        'remainingPayments',
        RemainingPaymentsTooltip,
      ),
      value: numRemainingPayments,
    },
    {
      label: getLabel(
        t(($) => $.pageContractSummaryEcomm.initialPayment),
        'initialPayment',
        InitialPaymentsTooltip,
      ),
      value: initialPayment,
    },

    {
      // TODO: ERK change payments 2 to be a variable.  Not always 2
      label: getLabel(`${t(($) => $.pageContractSummaryEcomm.payments2)} - ${numSimilarPayments}`, 'secondPayment', SecondPaymentsTooltip),
      value: secondPayment,
    },
    ...(finalPayment !== null
      ? [
          {
            label: getLabel(
              `${t(($) => $.pageContractSummaryEcomm.payment)} ${numSimilarPayments ? numSimilarPayments + 1 : ''}`,
              'finalPayment',
              FinalPaymentTooltip,
            ),
            value: finalPayment,
          },
        ]
      : []),

    ...(costOfLease !== null
      ? [
          {
            label: getLabel(
              t(($) => $.pageContractSummaryEcomm.costOfLease),
              'costOfLease',
              CostOfLeaseTooltip({ cpt: cashPlusTerm.singular, cpp: cashPlusPrice }),
            ),
            value: costOfLease,
          },
        ]
      : []),
    {
      label: getLabel(
        t(($) => $.pageContractSummaryEcomm.totalCost),
        'totalCost',
        TotalOwnershipTooltip({ cpt: cashPlusTerm.singular, cpp: cashPlusPrice }),
      ),
      value: costOfOwnership,
      makeBold: true,
    },
  ];

  return <Table name="contract-summary-table" rows={leaseRows} />;
};

export default ContractSummaryTable;
